import { Accordion, Grid, Heading } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { useOutletContext } from 'react-router-dom'
import { useState, useMemo, useCallback } from 'react'
import {
    formattedTimeStamp,
    buildINFRGDTables,
    getInfColumnIndices,
} from '../../helpers'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { useEssentials } from 'hooks/utils/useEssentials'
import { buildDataTableObject } from 'utils/data'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

const INF_VIEWS = ['pickup', 'ship'] as const
const TABLE_WIDTHS = [75, 75, 90, 80] as const

export const StoreFulfillmentInfRGD = () => {
    const { data } = useOutletContext<{ data: any }>()
    const pickupINFData = data[0]?.order_pickup_unit?.inf_details
    const shipINFData = data[0]?.ship_from_store?.inf_details
    const { t, currentLocation } = useEssentials()
    const [activeTab, setActiveTab] = useState(0)

    const { storeFulfillment } = TRANSLATION_KEYS
    const { inf } = storeFulfillment

    const tablePickupIndices = useMemo(
        () => getInfColumnIndices(pickupINFData),
        [pickupINFData],
    )
    const tableShipIndices = useMemo(
        () => getInfColumnIndices(shipINFData),
        [shipINFData],
    )

    const totalHeadings = [
        t(inf.rgdHeadings.pickup.totalPickupInf),
        t(inf.rgdHeadings.ship.totalShipINF),
    ]
    const expandableHeadings = [
        [
            t(inf.rgdHeadings.pickup.pickupINFApparel),
            t(inf.rgdHeadings.pickup.pickupINFBeauty),
            t(inf.rgdHeadings.pickup.pickupINFFoodBeverage),
            t(inf.rgdHeadings.pickup.pickupINFHardlines),
            t(inf.rgdHeadings.pickup.pickupINFHome),
        ],
        [
            t(inf.rgdHeadings.ship.shipINFApparel),
            t(inf.rgdHeadings.ship.shipINFBeauty),
            t(inf.rgdHeadings.ship.shipINFFoodBeverage),
            t(inf.rgdHeadings.ship.shipINFHardlines),
            t(inf.rgdHeadings.ship.shipINFHome),
        ],
    ]
    const todayLabelKey = TRANSLATION_KEYS.storeFulfillment.timePeriods.today

    const { formattedDate, formattedTables } = useMemo(() => {
        const tableHeaders = [
            t(inf.rgdHeadings.tableHeaders.units),
            t(inf.rgdHeadings.tableHeaders.dollars),
            t(inf.rgdHeadings.tableHeaders.inf),
        ]

        const timestamp = formattedTimeStamp(pickupINFData?.timestamp)
        const formattedDate = `${t(storeFulfillment.titles.todayAsOf)} ${timestamp}`

        const rawTables = [
            buildINFRGDTables(
                pickupINFData,
                tableHeaders,
                currentLocation,
                tablePickupIndices,
                t(storeFulfillment.headings.location),
            ),
            buildINFRGDTables(
                shipINFData,
                tableHeaders,
                currentLocation,
                tableShipIndices,
                t(storeFulfillment.headings.location),
            ),
        ]

        const formatted = rawTables[activeTab]?.map((tableObj) =>
            tableObj.table
                ? buildDataTableObject({
                      currentData: tableObj.table,
                      widths: Array.from(TABLE_WIDTHS),
                      sortable: false,
                  })
                : null,
        )

        return { formattedDate, formattedTables: formatted }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        t,
        inf.rgdHeadings.tableHeaders.units,
        inf.rgdHeadings.tableHeaders.dollars,
        inf.rgdHeadings.tableHeaders.inf,
        storeFulfillment.headings.location,
        pickupINFData,
        todayLabelKey,
        currentLocation,
        tablePickupIndices,
        shipINFData,
        tableShipIndices,
        activeTab,
    ])

    const handleTabChange = useCallback((index: number) => {
        setActiveTab(index)
    }, [])

    return (
        <Grid.Container>
            <Grid.Item
                className="hc8-pt-4x hc8-pl-none hc8-pr-none"
                size={{
                    xs: 12,
                }}
            >
                {formattedDate}
                <GeneralizedComponent.DataTabs
                    tabs={[...INF_VIEWS]}
                    tabsIndex={activeTab}
                    setTabsIndex={handleTabChange}
                    logClick={() => {}}
                    kpiName="storeFulfillmentInfRGD"
                />
            </Grid.Item>
            <Grid.Item
                className="hc8-pt-none"
                size={{
                    xs: 12,
                }}
            >
                <Heading size={6} className="hc8-pa-none">
                    {totalHeadings[activeTab]}
                </Heading>
            </Grid.Item>
            <Grid.Item
                className="hc8-pa-2x"
                size={{
                    xs: 12,
                }}
            >
                {formattedTables?.[0] && (
                    <Table data={formattedTables[0]} cellPadding="normal" />
                )}
            </Grid.Item>
            {expandableHeadings[activeTab].map((heading, idx) => (
                <Grid.Item
                    key={heading}
                    className="hc8-ph-none"
                    size={{
                        xs: 12,
                    }}
                >
                    <Accordion spacing="dense" header={heading}>
                        {formattedTables?.[idx + 1] && (
                            <Table
                                data={formattedTables[idx + 1]}
                                cellPadding="normal"
                            />
                        )}
                    </Accordion>
                </Grid.Item>
            ))}
        </Grid.Container>
    )
}
